import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { StyledProps, ColorProps } from '../types/styled-utils';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 6rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    padding: 6rem 1rem 2rem;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.lg}) {
    flex-direction: column;
    gap: 3rem;
    text-align: center;
  }
`;

const HeroText = styled.div`
  flex: 1;
  max-width: 600px;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.lg}) {
    max-width: 100%;
    order: 2;
  }
`;

const HeroHeading = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  span {
    background: linear-gradient(
      to right,
      ${(props: StyledProps) => props.theme.colors.gradientStart},
      ${(props: StyledProps) => props.theme.colors.gradientEnd}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const HeroSubheading = styled.h2`
  font-size: 1.5rem;
  font-weight: 400;
  margin-bottom: 2rem;
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  line-height: 1.5;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    font-size: 1.2rem;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.lg}) {
    justify-content: center;
  }
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled.a`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: linear-gradient(
    to right,
    ${(props: StyledProps) => props.theme.colors.gradientStart},
    ${(props: StyledProps) => props.theme.colors.gradientEnd}
  );
  color: white;
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(110, 86, 207, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(110, 86, 207, 0.5);
  }
`;

const SecondaryButton = styled.a`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: transparent;
  color: ${(props: StyledProps) => props.theme.colors.primary};
  font-weight: 600;
  border-radius: 4px;
  border: 2px solid ${(props: StyledProps) => props.theme.colors.primary};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(110, 86, 207, 0.1);
    transform: translateY(-3px);
  }
`;

const HeroVisual = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.lg}) {
    order: 1;
    width: 100%;
  }
`;

const TerminalWindow = styled(motion.div)`
  width: 100%;
  max-width: 600px;
  height: 400px;
  background: rgba(18, 20, 37, 0.05);
  border-radius: 6px;
  backdrop-filter: blur(3px);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.07);
  position: relative;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    height: 300px;
  }
`;

const TerminalHeader = styled.div`
  background: rgba(40, 40, 40, 0.05);
  padding: 0.8rem 0.75rem;
  display: flex;
  align-items: center;
`;

const WindowControls = styled.div`
  display: flex;
  gap: 0.5rem;
  position: absolute;
  left: 0.8rem;
`;

interface ButtonProps {
  color: string;
}

const WindowButton: React.FC<ButtonProps> = ({ color }) => {
  return (
    <div
      style={{
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: color
      }}
    />
  );
};

const TerminalTitle = styled.div`
  flex-grow: 1;
  justify-content: center;
  text-align: center;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
`;

const TerminalContent = styled.div`
  padding: 1rem;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 0.9rem;
  color: white;
  height: calc(100% - 45px);
  overflow: hidden;
`;

const TerminalLine = styled(motion.div)`
  margin-bottom: 0.5rem;
  display: flex;
`;

const Prompt = styled.span`
  color: #87D441;
  margin-right: 0.5rem;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const Command = styled.span`
  color: #FFFFFF;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
`;

const TerminalOutput = styled(motion.div)`
  color: #F0F0F0;
  margin-left: 1rem;
  margin-bottom: 1rem;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
  
  a {
    color: #87D441; /* Green link color to match prompt */
    text-decoration: underline;
    transition: color 0.2s;
    
    &:hover {
      color: #a0ea5a;
      text-decoration: underline;
    }
  }
`;

const Cursor = styled(motion.span)`
  display: inline-block;
  width: 8px;
  height: 16px;
  background-color: #87D441;
  margin-left: 2px;
  vertical-align: middle;
`;

const convertLinksToHTML = (text: string) => {
  // Replace <br> tags with actual newlines to be processed properly
  const processedText = text.replace(/<br\s*\/?>/gi, '\n');
  
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // Split the text by newlines to preserve formatting
  const lines = processedText.split('\n');
  
  // Process each line
  return lines.map((line, lineIndex) => {
    // Replace URLs with clickable links
    const lineWithLinks = line.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
    
    // Return the line with a line break except for the last line
    return (
      <React.Fragment key={lineIndex}>
        <span dangerouslySetInnerHTML={{ __html: lineWithLinks }} />
        {lineIndex < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const Hero: React.FC = () => {
  return (
    <HeroSection id="home">
      <HeroContent>
        <HeroText>
          <HeroHeading>
            Music Producer & <span>Developer</span>
          </HeroHeading>
          <HeroSubheading>
            Creating music and code with passion. Check out my tracks or explore my terminal-inspired portfolio.
          </HeroSubheading>
          <ButtonsContainer>
            <PrimaryButton href="#music">Listen to Music</PrimaryButton>
            <SecondaryButton href="#terminal">Try Terminal</SecondaryButton>
          </ButtonsContainer>
        </HeroText>
        
        <HeroVisual>
          <TerminalWindow
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <TerminalHeader>
              <WindowControls>
                <WindowButton color="#FF5F57" />
                <WindowButton color="#FFBD2E" />
                <WindowButton color="#28CA41" />
              </WindowControls>
              <TerminalTitle>6bit@terminal ~ </TerminalTitle>
            </TerminalHeader>
            
            <TerminalContent>
              <TerminalLine
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                <Prompt>$</Prompt>
                <Command>whoami</Command>
              </TerminalLine>
              
              <TerminalOutput
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
              >
                Music producer and developer passionate about creating digital experiences.
              </TerminalOutput>
              
              <TerminalLine
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.2 }}
              >
                <Prompt>$</Prompt>
                <Command>project ecliptic</Command>
              </TerminalLine>
              
              <TerminalOutput
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.7 }}
              >
                {convertLinksToHTML('Project: Ecliptic\n\nAI-Powered Beat-Synchronized Video Editor\n\nWebsite: https://ecliptic.gg')}
              </TerminalOutput>
              
              <TerminalLine
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 3.4 }}
              >
                <Prompt>$</Prompt>
                <Command>music</Command>
              </TerminalLine>
              
              <TerminalOutput
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 3.9 }}
              >
                {convertLinksToHTML('Music Links:<br />- Spotify: https://open.spotify.com/artist/6jSs5ecozKPDZ3LNFwlHH7<br />- YouTube: https://www.youtube.com/channel/UCeYaEjzXGfBKx1uy7-r00jA')}
              </TerminalOutput>
              
              <TerminalLine
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 4.6 }}
              >
                <Prompt>$</Prompt>
                <Command>_</Command>
              </TerminalLine>
            </TerminalContent>
          </TerminalWindow>
        </HeroVisual>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero; 